import { FormInitializerReturnType } from 'src/Configuration/validation'
import { WallboxConfigurationModel, WallboxSettingsFormModel } from '../..'
import { WallboxOcppModel } from '../../models'
import {
  BidiConnectModel,
  CommonModel,
  DefaultE3dcModel,
  EasyConnectModel,
  EfyModel,
  MultiConnectModel,
  EdsnModel,
} from '../../models/wallbox-configuration/wallbox-configuration-settings-form.model'
import { WallboxType } from '../../dto/types'
import { ObjectSchema } from 'yup'
import {
  useWallboxBidiFormInitializer,
  useWallboxEfyFormInitializer,
  useWallboxEdsnFormInitializer,
  useWallboxCommonFormInitializer,
  useWallboxDefaultE3dcFormInitializer,
  useWallboxEasyConnectFormInitializer,
  useWallboxMultiConnectFormInitializer,
} from '.'

/** Schema thats used, to check for the dynamic setting-types, that depend on the wallbox-type */
type PartialWallboxSchema = ObjectSchema<DefaultE3dcModel | EasyConnectModel | BidiConnectModel | MultiConnectModel | EfyModel | EdsnModel>

/** The overall return-type of the validation-schema for the wallbox-settings */
type PartialWallboxSchemaReturnType =
  | (CommonModel & DefaultE3dcModel)
  | (CommonModel & EasyConnectModel)
  | (CommonModel & BidiConnectModel)
  | (CommonModel & MultiConnectModel)
  | (CommonModel & EfyModel)
  | (CommonModel & EdsnModel)

/**
 * Initializes the form for editing wallbox settings according to wallbox type
 * @param wallboxType Wallbox type
 * @param ocppSettings WallboxOcppModel
 * @param model WallboxConfigurationModel
 * @returns initialValues and validationSchema for wallbox settings form
 */
export const useWallboxSettingsFormInitializer = (
  wallboxType: WallboxType,
  ocppSettings: WallboxOcppModel | null,
  model?: WallboxConfigurationModel
): FormInitializerReturnType<PartialWallboxSchemaReturnType, WallboxSettingsFormModel> => {
  const { initialValues: initialCommonValues, validationSchema: validationCommonValue } = useWallboxCommonFormInitializer(model)
  const { initialValues: initialDefaultE3dcValues, validationSchema: validationDefaultE3dcValue } = useWallboxDefaultE3dcFormInitializer(model)
  const { initialValues: initialEasyConnectValues, validationSchema: validationEasyConnectValue } = useWallboxEasyConnectFormInitializer(model)
  const { initialValues: initialBidiValues, validationSchema: validationBidiValue } = useWallboxBidiFormInitializer(model)
  const { initialValues: initialMultiConnectValues, validationSchema: validationMultiConnectValue } = useWallboxMultiConnectFormInitializer(model)
  const { initialValues: initialEfyValues, validationSchema: validationEfyValue } = useWallboxEfyFormInitializer(ocppSettings, model)
  const { initialValues: initialEdsnValues, validationSchema: validationEdsnValue } = useWallboxEdsnFormInitializer(model)

  const initialValues = {
    ...initialCommonValues,
    ...initialDefaultE3dcValues,
    ...initialEasyConnectValues,
    ...initialBidiValues,
    ...initialMultiConnectValues,
    ...initialEfyValues,
    ...initialEdsnValues,
  }

  const schemaConfig: Record<WallboxType, PartialWallboxSchema> = {
    [WallboxType.DefaultE3dc]: validationDefaultE3dcValue,
    [WallboxType.EasyConnect]: validationEasyConnectValue,
    [WallboxType.Bidi]: validationBidiValue,
    [WallboxType.MultiConnect]: validationMultiConnectValue,
    [WallboxType.Efy]: validationEfyValue,
    [WallboxType.Farm]: validationDefaultE3dcValue,
    [WallboxType.OCPP]: validationDefaultE3dcValue,
    [WallboxType.EDSN]: validationEdsnValue,
  }

  const validationSchema = validationCommonValue.concat(schemaConfig[wallboxType]).when({
    is: () => wallboxType === WallboxType.EDSN,
    then: schema => schema.omit(['autoPhaseSwitchEnabled', 'ledIntensity']),
  })

  return { initialValues, validationSchema }
}
